import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ArrowLeft, AlertTriangle, Plus, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useAuthStore } from '@/store/authStore';
import { getDSRDetail, type DSRDetailData } from '@/services/db/dsrDetail';
import { canAmend, amendDeadline } from '@/utils/dsr';
import {
  updateRecord, insertRecord, softDeleteRecord, saveAmendmentRecord,
} from '@/services/db/dsrAmend';

// ── Field config types ─────────────────────────────────

type FieldType =
  | 'text' | 'number' | 'textarea' | 'select'
  | 'boolean' | 'datetime' | 'time' | 'date' | 'tags';

interface FieldConfig {
  key: string;
  label: string;
  type: FieldType;
  options?: { value: string; label: string }[];
}

interface SectionConfig {
  key: string;
  title: string;
  table: string;
  mode: 'single' | 'array';
  fields: FieldConfig[];
  recordLabel?: (r: Record<string, unknown>) => string;
}

// ── Helpers ────────────────────────────────────────────

const fmtLabel = (v: string) =>
  v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const opts = (values: string[]) =>
  values.map((v) => ({ value: v, label: fmtLabel(v) }));

// ── Section field configs ──────────────────────────────

const SECTION_CONFIGS: SectionConfig[] = [
  {
    key: 'dailySetup', title: 'Daily Setup', table: 'dsr_daily_setups', mode: 'single',
    fields: [
      { key: 'weather_condition', label: 'Weather Condition', type: 'text' },
      { key: 'wind_speed_knots', label: 'Wind Speed (knots)', type: 'number' },
      { key: 'visibility_nm', label: 'Visibility (NM)', type: 'number' },
      { key: 'sea_state_beaufort', label: 'Sea State (Beaufort)', type: 'number' },
      { key: 'temperature_celsius', label: 'Temperature (°F)', type: 'number' },
      { key: 'site_conditions_notes', label: 'Site Conditions', type: 'textarea' },
      { key: 'rig_activity_codes', label: 'Rig Activity Codes', type: 'tags' },
    ],
  },
  {
    key: 'personnel', title: 'Workforce', table: 'dsr_personnel_summaries', mode: 'array',
    fields: [
      { key: 'company_name', label: 'Company', type: 'text' },
      { key: 'crew_type', label: 'Crew Type', type: 'text' },
      { key: 'personnel_count', label: 'Headcount', type: 'number' },
      { key: 'supervisor_name', label: 'Supervisor', type: 'text' },
      { key: 'notes', label: 'Notes', type: 'textarea' },
    ],
    recordLabel: (r) => (r.company_name as string) || 'Crew Entry',
  },
  {
    key: 'activities', title: 'Activities', table: 'dsr_activities', mode: 'array',
    fields: [
      { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'status', label: 'Status', type: 'select', options: opts(['planned', 'in_progress', 'completed', 'cancelled', 'deferred']) },
      { key: 'risk_level', label: 'Risk Level', type: 'select', options: opts(['low', 'medium', 'high', 'critical']) },
      { key: 'start_time', label: 'Start Time', type: 'datetime' },
      { key: 'end_time', label: 'End Time', type: 'datetime' },
      { key: 'duration_minutes', label: 'Duration (min)', type: 'number' },
      { key: 'crew_size', label: 'Crew Size', type: 'number' },
      { key: 'is_jsa_required', label: 'JSA Required', type: 'boolean' },
      { key: 'jsa_completed', label: 'JSA Completed', type: 'boolean' },
      { key: 'is_complete', label: 'Complete', type: 'boolean' },
      { key: 'risk_factor_category', label: 'Risk Factor Category', type: 'tags' },
      { key: 'risk_tags', label: 'Risk Tags', type: 'tags' },
      { key: 'risk_justification', label: 'Risk Justification', type: 'textarea' },
      { key: 'notes', label: 'Notes', type: 'textarea' },
    ],
    recordLabel: (r) => ((r.description as string) || 'Activity').substring(0, 60),
  },
  {
    key: 'equipment', title: 'Equipment', table: 'dsr_equipment', mode: 'array',
    fields: [
      { key: 'equipment_name', label: 'Equipment Name', type: 'text' },
      { key: 'equipment_type', label: 'Type', type: 'text' },
      { key: 'equipment_identifier', label: 'Identifier', type: 'text' },
      { key: 'status', label: 'Status', type: 'select', options: opts(['active', 'standby', 'down', 'removed']) },
      { key: 'hours_operated', label: 'Hours Operated', type: 'number' },
      { key: 'operator_crew', label: 'Operator/Crew', type: 'text' },
      { key: 'notes', label: 'Notes', type: 'textarea' },
    ],
    recordLabel: (r) => (r.equipment_name as string) || 'Equipment',
  },
  {
    key: 'hseObservations', title: 'HSE Observations (Good Catch)', table: 'dsr_hse_observations', mode: 'array',
    fields: [
      { key: 'observation_type', label: 'Type', type: 'select', options: opts(['positive', 'at_risk', 'pta']) },
      { key: 'observation_category', label: 'Category', type: 'text' },
      { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'observation_time', label: 'Time', type: 'time' },
      { key: 'location_detail', label: 'Location', type: 'text' },
      { key: 'associated_crew', label: 'Crew', type: 'text' },
      { key: 'observed_by', label: 'Observed By', type: 'text' },
      { key: 'corrective_action', label: 'Corrective Action', type: 'textarea' },
      { key: 'is_resolved', label: 'Resolved', type: 'boolean' },
      { key: 'comments', label: 'Comments', type: 'textarea' },
    ],
    recordLabel: (r) => ((r.description as string) || 'Observation').substring(0, 60),
  },
  {
    key: 'permits', title: 'Permits', table: 'dsr_permit_entries', mode: 'array',
    fields: [
      { key: 'permit_type', label: 'Permit Type', type: 'select', options: opts(['hot_work', 'confined_space_entry', 'critical_lift', 'electrical_isolation', 'loto', 'excavation', 'overwater_work', 'radiation', 'general_work', 'other']) },
      { key: 'permit_number', label: 'Permit #', type: 'text' },
      { key: 'status', label: 'Status', type: 'select', options: opts(['open', 'active', 'suspended', 'closed', 'cancelled']) },
      { key: 'issued_by', label: 'Issued By', type: 'text' },
      { key: 'issued_at', label: 'Issued At', type: 'datetime' },
      { key: 'expires_at', label: 'Expires At', type: 'datetime' },
      { key: 'work_location', label: 'Work Location', type: 'text' },
      { key: 'is_verified', label: 'Verified', type: 'boolean' },
      { key: 'activity_description', label: 'Activity', type: 'text' },
      { key: 'comments', label: 'Comments', type: 'textarea' },
    ],
    recordLabel: (r) => (r.permit_number as string) || fmtLabel((r.permit_type as string) ?? 'Permit'),
  },
  {
    key: 'meetings', title: 'Safety Meetings', table: 'dsr_meeting_entries', mode: 'array',
    fields: [
      { key: 'meeting_type', label: 'Type', type: 'select', options: opts(['toolbox_talk', 'pre_job_safety_meeting', 'jsa_review', 'incident_review', 'emergency_drill_briefing', 'shift_change_briefing', 'other']) },
      { key: 'topic', label: 'Topic', type: 'text' },
      { key: 'meeting_time', label: 'Time', type: 'time' },
      { key: 'duration_minutes', label: 'Duration (min)', type: 'number' },
      { key: 'attendee_count', label: 'Attendees', type: 'number' },
      { key: 'conducted_by', label: 'Conducted By', type: 'text' },
      { key: 'key_points', label: 'Key Points', type: 'textarea' },
      { key: 'activity_description', label: 'Activity', type: 'text' },
      { key: 'comments', label: 'Comments', type: 'textarea' },
    ],
    recordLabel: (r) => (r.topic as string) || fmtLabel((r.meeting_type as string) ?? 'Meeting'),
  },
  {
    key: 'jsaEvaluations', title: 'JSA Evaluations', table: 'dsr_jsa_evaluations', mode: 'array',
    fields: [
      { key: 'jsa_reference', label: 'JSA Reference', type: 'text' },
      { key: 'activity_description', label: 'Activity', type: 'text' },
      { key: 'work_crew', label: 'Work Crew', type: 'text' },
      { key: 'adequacy_rating', label: 'Adequacy', type: 'select', options: opts(['adequate', 'adequate_with_comments', 'inadequate']) },
      { key: 'hazards_identified_count', label: 'Hazards Count', type: 'number' },
      { key: 'controls_in_place', label: 'Controls', type: 'textarea' },
      { key: 'process_adherence', label: 'Adherence', type: 'select', options: opts(['following_jsa', 'minor_deviations', 'not_following_jsa']) },
      { key: 'corrective_action', label: 'Corrective Action', type: 'textarea' },
      { key: 'observer_name', label: 'Observer', type: 'text' },
      { key: 'comments', label: 'Comments', type: 'textarea' },
    ],
    recordLabel: (r) => (r.jsa_reference as string) || (r.activity_description as string) || 'JSA',
  },
  {
    key: 'swaEvents', title: 'SWA Events', table: 'dsr_swa_events', mode: 'array',
    fields: [
      { key: 'severity', label: 'Severity', type: 'select', options: opts(['minor', 'moderate', 'serious', 'critical']) },
      { key: 'work_status', label: 'Work Status', type: 'select', options: opts(['stopped', 'resumed', 'ongoing']) },
      { key: 'invoked_at', label: 'Invoked At', type: 'datetime' },
      { key: 'invoked_by', label: 'Invoked By', type: 'text' },
      { key: 'site_location', label: 'Location', type: 'text' },
      { key: 'hazard_description', label: 'Hazard', type: 'textarea' },
      { key: 'action_taken', label: 'Action Taken', type: 'textarea' },
      { key: 'activity_description', label: 'Activity', type: 'text' },
      { key: 'work_crew', label: 'Work Crew', type: 'text' },
      { key: 'personnel_count', label: 'Personnel Count', type: 'number' },
      { key: 'is_supervisor_notified', label: 'Supervisor Notified', type: 'boolean' },
      { key: 'supervisor_name', label: 'Supervisor Name', type: 'text' },
      { key: 'is_management_notified', label: 'Mgmt Notified', type: 'boolean' },
      { key: 'category', label: 'Categories', type: 'tags' },
      { key: 'outcome', label: 'Outcome', type: 'textarea' },
      { key: 'authorized_resumption_by', label: 'Resumption Auth By', type: 'text' },
      { key: 'resumption_time', label: 'Resumption Time', type: 'datetime' },
      { key: 'lessons_learned', label: 'Lessons Learned', type: 'textarea' },
    ],
    recordLabel: (r) => ((r.hazard_description as string) || 'SWA Event').substring(0, 50),
  },
  {
    key: 'safetyEvents', title: 'Safety Events', table: 'dsr_safety_events', mode: 'array',
    fields: [
      { key: 'event_type', label: 'Event Type', type: 'select', options: opts(['near_miss', 'first_aid', 'recordable_injury', 'lost_time_injury', 'property_damage', 'environmental_release', 'security_incident']) },
      { key: 'event_title', label: 'Title', type: 'text' },
      { key: 'event_description', label: 'Description', type: 'textarea' },
      { key: 'event_datetime', label: 'Date/Time', type: 'datetime' },
      { key: 'location_area', label: 'Location', type: 'text' },
      { key: 'immediate_actions', label: 'Immediate Actions', type: 'textarea' },
      { key: 'category', label: 'Categories', type: 'tags' },
      { key: 'is_work_stopped', label: 'Work Stopped', type: 'boolean' },
      { key: 'personnel_count', label: 'Personnel Count', type: 'number' },
      { key: 'activity_description', label: 'Activity', type: 'text' },
      { key: 'is_supervisor_notified', label: 'Supervisor Notified', type: 'boolean' },
      { key: 'is_management_notified', label: 'Mgmt Notified', type: 'boolean' },
      { key: 'is_regulatory_required', label: 'Regulatory Required', type: 'boolean' },
      { key: 'contractor_crew', label: 'Contractor Crew', type: 'text' },
      { key: 'near_miss_potential_severity', label: 'Near Miss Severity', type: 'text' },
      { key: 'near_miss_conditions', label: 'Near Miss Conditions', type: 'textarea' },
      { key: 'injured_person_role', label: 'Injured Person Role', type: 'text' },
      { key: 'injury_type', label: 'Injury Type', type: 'text' },
      { key: 'body_part_affected', label: 'Body Part', type: 'text' },
      { key: 'treatment_provided', label: 'Treatment', type: 'text' },
    ],
    recordLabel: (r) => (r.event_title as string) || fmtLabel((r.event_type as string) ?? 'Safety Event'),
  },
  {
    key: 'forecastedActivities', title: 'Forecasted Activities', table: 'dsr_forecasted_activities', mode: 'array',
    fields: [
      { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'anticipated_risk', label: 'Risk Level', type: 'select', options: opts(['low', 'medium', 'high']) },
      { key: 'risk_notes', label: 'Risk Notes', type: 'textarea' },
      { key: 'activity_tags', label: 'Tags', type: 'tags' },
      { key: 'is_permits_required', label: 'Permits Required', type: 'boolean' },
      { key: 'estimated_duration', label: 'Est. Duration (hrs)', type: 'number' },
      { key: 'notes', label: 'Notes', type: 'textarea' },
    ],
    recordLabel: (r) => ((r.description as string) || 'Forecasted Activity').substring(0, 50),
  },
  {
    key: 'outstandingItems', title: 'Outstanding Items', table: 'dsr_outstanding_items', mode: 'array',
    fields: [
      { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'priority', label: 'Priority', type: 'select', options: opts(['high', 'medium', 'low']) },
    ],
    recordLabel: (r) => ((r.description as string) || 'Outstanding Item').substring(0, 50),
  },
  {
    key: 'forecastingSummary', title: 'Forecasting Summary', table: 'dsr_forecasting_summaries', mode: 'single',
    fields: [
      { key: 'overall_risk_assessment', label: 'Overall Risk', type: 'select', options: opts(['low_risk', 'elevated_risk', 'high_risk', 'critical_risk']) },
      { key: 'risk_comments', label: 'Risk Comments', type: 'textarea' },
      { key: 'resource_adequacy', label: 'Resource Adequacy', type: 'select', options: opts(['fully_adequate', 'minor_gaps', 'significant_gaps', 'inadequate']) },
      { key: 'resource_comments', label: 'Resource Comments', type: 'textarea' },
      { key: 'next_shift_narrative', label: 'Next Shift Narrative', type: 'textarea' },
    ],
  },
  {
    key: 'narrative', title: 'Shift Narrative', table: 'dsr_narratives', mode: 'single',
    fields: [
      { key: 'content', label: 'Narrative Content', type: 'textarea' },
    ],
  },
  {
    key: 'actionItems', title: 'Action Items', table: 'dsr_action_items', mode: 'array',
    fields: [
      { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'assigned_to_name', label: 'Assigned To', type: 'text' },
      { key: 'due_date', label: 'Due Date', type: 'date' },
      { key: 'status', label: 'Status', type: 'select', options: opts(['open', 'in_progress', 'closed', 'overdue']) },
      { key: 'priority', label: 'Priority', type: 'select', options: opts(['low', 'medium', 'high', 'critical']) },
      { key: 'resolution_notes', label: 'Resolution Notes', type: 'textarea' },
    ],
    recordLabel: (r) => ((r.description as string) || 'Action Item').substring(0, 50),
  },
];

// ── Generic field editor ───────────────────────────────

function EditField({
  config,
  value,
  onChange,
}: {
  config: FieldConfig;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  switch (config.type) {
    case 'text':
      return (
        <Input
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
        />
      );
    case 'number':
      return (
        <Input
          type="number"
          value={value != null ? String(value) : ''}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        />
      );
    case 'textarea':
      return (
        <Textarea
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          rows={2}
        />
      );
    case 'select':
      return (
        <Select value={(value as string) ?? ''} onValueChange={(v) => onChange(v || null)}>
          <SelectTrigger>
            <SelectValue placeholder="Select..." />
          </SelectTrigger>
          <SelectContent>
            {config.options?.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    case 'boolean':
      return (
        <div className="pt-1">
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300"
          />
        </div>
      );
    case 'datetime': {
      let inputVal = '';
      if (value) {
        const d = new Date(value as string);
        if (!isNaN(d.getTime())) {
          const pad = (n: number) => String(n).padStart(2, '0');
          inputVal = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
        }
      }
      return (
        <Input
          type="datetime-local"
          value={inputVal}
          onChange={(e) => onChange(e.target.value ? new Date(e.target.value).toISOString() : null)}
        />
      );
    }
    case 'time':
      return (
        <Input
          type="time"
          value={value ? String(value).substring(0, 5) : ''}
          onChange={(e) => onChange(e.target.value ? e.target.value + ':00' : null)}
        />
      );
    case 'date':
      return (
        <Input
          type="date"
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
        />
      );
    case 'tags':
      return (
        <Input
          value={Array.isArray(value) ? (value as string[]).join(', ') : ''}
          onChange={(e) =>
            onChange(
              e.target.value
                ? e.target.value.split(',').map((s) => s.trim()).filter(Boolean)
                : []
            )
          }
          placeholder="Comma-separated values"
        />
      );
    default:
      return null;
  }
}

// ── Main component ─────────────────────────────────────

export default function DSRAmendPage() {
  const { dsrId } = useParams<{ dsrId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const userRole = useAuthStore((s) => s.userRole);
  const tenantId = useAuthStore((s) => s.tenantId);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['dsr_detail', dsrId],
    queryFn: () => getDSRDetail(dsrId!),
    enabled: !!dsrId,
    staleTime: 5 * 60 * 1000,
  });

  // ── Amendment state ──────────────────────────────────
  const [reason, setReason] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [sections, setSections] = useState<Record<string, any>>({});
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const originalRef = useRef<Record<string, any>>({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize section state from loaded DSR data
  useEffect(() => {
    if (!data) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const initial: Record<string, any> = {};
    const expand = new Set<string>();

    for (const config of SECTION_CONFIGS) {
      const raw = data[config.key as keyof DSRDetailData];
      if (config.mode === 'single') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        initial[config.key] = raw ? { ...(raw as any) } : null;
        if (raw) expand.add(config.key);
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const arr = (raw as any[]) ?? [];
        initial[config.key] = arr.map((r) => ({ ...r }));
        if (arr.length > 0) expand.add(config.key);
      }
    }

    setSections(initial);
    originalRef.current = JSON.parse(JSON.stringify(initial));
    setExpanded(expand);
  }, [data]);

  // ── Countdown timer ──────────────────────────────────
  const deadline = data?.report.submitted_at
    ? amendDeadline(data.report.submitted_at)
    : null;

  const [countdown, setCountdown] = useState('');
  useEffect(() => {
    if (!deadline) return;
    function tick() {
      const diff = deadline!.getTime() - Date.now();
      if (diff <= 0) { setCountdown('expired'); return; }
      const h = Math.floor(diff / 3_600_000);
      const m = Math.floor((diff % 3_600_000) / 60_000);
      setCountdown(`${h}h ${m}m remaining`);
    }
    tick();
    const id = setInterval(tick, 60_000);
    return () => clearInterval(id);
  }, [deadline]);

  // ── Auth check ───────────────────────────────────────
  const amendAllowed = data && canAmend(data.report, user?.id ?? '', userRole ?? '');

  // ── State handlers ───────────────────────────────────

  function toggleSection(key: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  function updateSingleField(sectionKey: string, fieldKey: string, value: unknown) {
    setSections((prev) => ({
      ...prev,
      [sectionKey]: { ...prev[sectionKey], [fieldKey]: value },
    }));
  }

  function updateArrayField(sectionKey: string, index: number, fieldKey: string, value: unknown) {
    setSections((prev) => ({
      ...prev,
      [sectionKey]: prev[sectionKey].map(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (r: any, i: number) => (i === index ? { ...r, [fieldKey]: value } : r)
      ),
    }));
  }

  function addRecord(sectionKey: string) {
    const defaults: Record<string, unknown> = { id: crypto.randomUUID(), _isNew: true };
    if (sectionKey === 'activities') {
      defaults.sequence_order = (sections[sectionKey]?.length ?? 0) + 1;
    }
    setSections((prev) => ({
      ...prev,
      [sectionKey]: [...(prev[sectionKey] ?? []), defaults],
    }));
  }

  function removeRecord(sectionKey: string, index: number) {
    setSections((prev) => ({
      ...prev,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      [sectionKey]: prev[sectionKey].filter((_: any, i: number) => i !== index),
    }));
  }

  // ── Save handler ─────────────────────────────────────

  async function handleSave() {
    if (reason.length < 10 || !dsrId || !user || !tenantId) return;

    // Guard against expired window
    if (deadline && Date.now() > deadline.getTime()) {
      setError('Amendment window has expired.');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      for (const config of SECTION_CONFIGS) {
        const current = sections[config.key];
        const original = originalRef.current[config.key];

        if (config.mode === 'single') {
          if (!current) continue;
          const payload: Record<string, unknown> = {};
          for (const f of config.fields) payload[f.key] = current[f.key] ?? null;

          if (current.id) {
            await updateRecord(config.table, current.id, payload);
          } else {
            await insertRecord(config.table, {
              ...payload,
              dsr_report_id: dsrId,
              tenant_id: tenantId,
              created_by: user.id,
            });
          }
        } else {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const currentRecords: any[] = current ?? [];
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const originalRecords: any[] = original ?? [];
          const currentExistingIds = new Set(
            currentRecords.filter((r) => !r._isNew).map((r) => r.id)
          );

          for (const record of currentRecords) {
            const payload: Record<string, unknown> = {};
            for (const f of config.fields) payload[f.key] = record[f.key] ?? null;

            if (record._isNew) {
              if (config.key === 'activities') {
                payload.sequence_order = record.sequence_order ?? 1;
              }
              await insertRecord(config.table, {
                ...payload,
                dsr_report_id: dsrId,
                tenant_id: tenantId,
                created_by: user.id,
              });
            } else {
              await updateRecord(config.table, record.id, payload);
            }
          }

          // Soft-delete removed records
          for (const orig of originalRecords) {
            if (!currentExistingIds.has(orig.id)) {
              await softDeleteRecord(config.table, orig.id);
            }
          }
        }
      }

      await saveAmendmentRecord(dsrId, user.id, tenantId, reason);
      queryClient.invalidateQueries({ queryKey: ['dsr_detail', dsrId] });
      navigate(`/reports/${dsrId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save amendment');
    } finally {
      setIsSaving(false);
    }
  }

  // ── Loading / error / unauthorized states ────────────

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-6 w-64" />
        <Skeleton className="h-4 w-48" />
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-32 w-full" />
        ))}
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex flex-col items-center gap-4 py-20">
        <p className="text-sm text-muted-foreground">Could not load this DSR report.</p>
        <Button variant="outline" onClick={() => navigate('/reports')}>
          Back to Reports
        </Button>
      </div>
    );
  }

  if (!amendAllowed) {
    return (
      <div className="space-y-4 py-10">
        <p className="text-sm text-destructive">
          This report cannot be amended. The amendment window may have expired or you do not have permission.
        </p>
        <Button variant="outline" onClick={() => navigate(`/reports/${dsrId}`)}>
          Back to Report
        </Button>
      </div>
    );
  }

  const { report } = data;
  const project = report.projects;

  // ── Render ───────────────────────────────────────────

  return (
    <div className="space-y-4 pb-24">
      {/* Back link */}
      <button
        onClick={() => navigate(`/reports/${dsrId}`)}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Report
      </button>

      {/* Header */}
      <div className="space-y-1">
        <h1 className="text-lg font-semibold">
          Amending: {project?.name ?? 'Unknown Project'} —{' '}
          {format(new Date(report.report_date), 'MMM d, yyyy')}
        </h1>
        <p className="text-sm capitalize text-muted-foreground">{report.shift_type} Shift</p>
      </div>

      {/* Warning banner with countdown */}
      <Card className="border-mozaic-orange/40 bg-mozaic-orange/5">
        <CardContent className="py-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-mozaic-orange" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-mozaic-orange">
                Amendments are tracked. This report was submitted{' '}
                {report.submitted_at &&
                  format(new Date(report.submitted_at), "MMM d, yyyy 'at' HH:mm")}
                .
              </p>
              {deadline && countdown !== 'expired' && (
                <p className="text-xs text-mozaic-orange/80">
                  Amendment window closes at{' '}
                  <strong>{format(deadline, "MMM d, yyyy 'at' HH:mm")}</strong>.{' '}
                  <span className="font-semibold">{countdown}</span>
                </p>
              )}
              {countdown === 'expired' && (
                <p className="text-xs font-semibold text-destructive">
                  Amendment window has expired. You cannot save changes.
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Amendment reason */}
      <Card>
        <CardHeader>
          <CardTitle>Reason for Amendment *</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Describe why this report needs to be amended (min 10 characters)..."
            rows={3}
            maxLength={500}
          />
          <p className="mt-1 text-xs text-muted-foreground">
            {reason.length}/500 characters
            {reason.length > 0 && reason.length < 10 && ' — minimum 10 required'}
          </p>
        </CardContent>
      </Card>

      {/* Editable sections */}
      {SECTION_CONFIGS.map((config) => {
        const sectionData = sections[config.key];
        const isExpanded = expanded.has(config.key);
        const recordCount = config.mode === 'array' ? (sectionData?.length ?? 0) : null;
        const hasData = config.mode === 'single' ? !!sectionData : recordCount! > 0;

        return (
          <Card key={config.key}>
            <CardHeader
              className="cursor-pointer select-none"
              onClick={() => toggleSection(config.key)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CardTitle>{config.title}</CardTitle>
                  {recordCount != null && (
                    <Badge className="bg-mozaic-navy/10 text-mozaic-navy">{recordCount}</Badge>
                  )}
                  {!hasData && <span className="text-xs text-muted-foreground">— empty</span>}
                </div>
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
              </div>
            </CardHeader>

            {isExpanded && (
              <CardContent>
                {config.mode === 'single' ? (
                  sectionData ? (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {config.fields.map((field) => (
                        <div
                          key={field.key}
                          className={
                            field.type === 'textarea' || field.type === 'tags'
                              ? 'sm:col-span-2'
                              : ''
                          }
                        >
                          <Label className="text-xs">{field.label}</Label>
                          <EditField
                            config={field}
                            value={sectionData[field.key]}
                            onChange={(v) => updateSingleField(config.key, field.key, v)}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No data recorded.</p>
                  )
                ) : (
                  <div className="space-y-4">
                    {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                    {(sectionData ?? []).map((record: any, index: number) => (
                      <Card key={record.id} size="sm" className="bg-muted/30">
                        <CardHeader>
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">
                              {config.recordLabel?.(record) ?? `Record ${index + 1}`}
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 text-destructive hover:text-destructive"
                              onClick={() => removeRecord(config.key, index)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            {config.fields.map((field) => (
                              <div
                                key={field.key}
                                className={
                                  field.type === 'textarea' || field.type === 'tags'
                                    ? 'sm:col-span-2'
                                    : ''
                                }
                              >
                                <Label className="text-xs">{field.label}</Label>
                                <EditField
                                  config={field}
                                  value={record[field.key]}
                                  onChange={(v) => updateArrayField(config.key, index, field.key, v)}
                                />
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                    <Button variant="outline" size="sm" onClick={() => addRecord(config.key)}>
                      <Plus className="mr-1 h-3.5 w-3.5" />
                      Add {config.title.replace(/s$/, '')}
                    </Button>
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        );
      })}

      {/* Error message */}
      {error && (
        <Card className="border-destructive/40 bg-destructive/5">
          <CardContent className="py-3">
            <p className="text-sm text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Sticky save/cancel bar */}
      <div className="sticky bottom-0 z-20 -mx-4 border-t bg-background px-4 py-4 sm:-mx-6 sm:px-6">
        <div className="flex items-center justify-end gap-3">
          <Button
            variant="outline"
            onClick={() => navigate(`/reports/${dsrId}`)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            className="bg-mozaic-orange hover:bg-mozaic-orange/90"
            disabled={isSaving || reason.length < 10 || countdown === 'expired'}
            onClick={handleSave}
          >
            {isSaving ? 'Saving...' : 'Save Amendment'}
          </Button>
        </div>
      </div>
    </div>
  );
}
